import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, MenuItem, Tooltip } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { UserDetails } from "./User";
import { currentlyValid, formatValidity } from "../../shared/technical/domain/ValidityInfo";

/**
 * Shows whether a user may log in. The validity period is shown as a hint, because it can keep
 * the user deactivated even though he was activated.
 */
export function UserEnabledStatus(props: {
  user: UserDetails,
}) {
  const { user } = props;
  const { t } = useTranslation();
  const hasValidity = !!user.validityInfo?.validFrom || !!user.validityInfo?.validTo;

  return (
    <Box sx={ {display: 'flex', alignItems: 'center', height: '100%'} }>
      <Tooltip title={ hasValidity && user.validityInfo ? formatValidity(user.validityInfo, t) : '' }>
        <Chip size="small"
              color={ user.enabled ? 'success' : 'error' }
              icon={ user.enabled ? <CheckCircleOutlineIcon /> : <HighlightOffIcon /> }
              label={ t(user.enabled ? 'User.statusEnabled' : 'User.statusDisabled') } />
      </Tooltip>
    </Box>
  );
}

/**
 * Menu entry to activate or deactivate a user. The validity period is never changed by this.
 */
export function UserEnabledMenuEntry(props: {
  user: UserDetails,
  onToggle: () => void,
}) {
  const { user, onToggle } = props;
  const { t } = useTranslation();
  const blockedByValidity = !user.enabled && !currentlyValid(user.validityInfo);

  return (
    <Tooltip title={ blockedByValidity ? t('UserPage.msgUserNotEnabledBecauseOfValidity') : '' } placement="left">
      <span>
        <MenuItem onClick={ onToggle } disabled={ blockedByValidity }>
          { t(user.enabled ? 'UserPage.menuDisableUser' : 'UserPage.menuEnableUser') }
        </MenuItem>
      </span>
    </Tooltip>
  );
}
